import React, { useEffect, useRef, useState } from "react";
import { UPDATE_CLIENT_NOTE } from "../../../../utils/GraphQL/mutations";
import "./updateNoteModal.css";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { useMutation } from "@apollo/client";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";

const clearedNote = {
  note: "",
  dateCreated: "",
  dateUpdated: "",
  updatedBy: "",
  createdBy: "",
};

const UpdateNoteModal = ({ updateCB, incNote, open, close, userName }) => {
  const [updateNoteCall] = useMutation(UPDATE_CLIENT_NOTE);
  const [noteObj, setNoteObj] = useState({ ...clearedNote });
  const [error, setError] = useState("");
  const inputRef = useRef();
  const [selectionRange, setSelectionRange] = useState(null);

  useEffect(() => {
    setNoteObj({ ...incNote });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incNote]);

  // Body scroll lock moved into BdrModal, where it is reference-counted. The
  // version here set `overflow: auto` on every render with open=false — this
  // component is mounted unconditionally by ClientNotesDisplay, so it also ran
  // on mount and would unlock scrolling for any other modal already open.

  useEffect(() => {
    if (selectionRange) {
      inputRef?.current?.setSelectionRange(selectionRange, selectionRange);
    }
  }, [selectionRange]);

  const handleChange = (event) => {
    let target = event.target;
    let value = target.value;
    let name = target.name;
    let selectionRangeVal = null;

    //regex to look behind and only find double asterisk that
    //isn't preceded by another asterisk. only finding the closing asterisks.
    const breakOutOfAsterisk = (prevNote, forwardBool) => {
      const regex = forwardBool
        ? /(?<=\*\*)([ \t]?\w{1}[\w\W\s]*?)(?=\*{2})/gm
        : /\*{4}/gim;
      const cursorStart = forwardBool
        ? target.selectionStart - 1
        : target.selectionStart;
      const matched = prevNote?.match(regex);
      const indexOfObjArr = matched?.map((str) => {
        const start = prevNote?.indexOf(str);
        const end = start + str?.length;
        return { start, end };
      });
      // console.log("matched", matched);
      // console.log(indexOfObjArr);
      // console.log(cursorStart);

      let returnedItem = { cursorPos: null, prevNote: null };

      indexOfObjArr?.forEach((obj) => {
        let splicedNote = "";

        if (forwardBool) {
          if (obj?.start <= cursorStart && obj?.end >= cursorStart) {
            const afterAsterisk = obj?.end + 2;
            splicedNote =
              prevNote?.slice(0, afterAsterisk) +
              "\n" +
              prevNote?.slice(afterAsterisk);
            returnedItem = {
              cursorPos: afterAsterisk + 1,
              prevNote: splicedNote,
            };
          }
        }

        if (!forwardBool) {
          if (obj?.start <= cursorStart && obj?.end > cursorStart) {
            splicedNote =
              prevNote?.slice(0, obj?.start) + prevNote?.slice(obj?.end);
            returnedItem = {
              cursorPos: obj?.start,
              prevNote: splicedNote,
            };
          }
        }
      });

      return returnedItem;
    };

    // Regex test for triple asterisk shortcut to
    // set double asterisk start and end and cursor middle
    // const shortcutRegex = mediaOver900
    //   ? /(?<!\*)\*{3}(?!\*+)/gi
    //   : /(?<![c])[c]{3}(?![c]+)/gi;
    const shortcutRegex = /(?<![c])[c]{3}(?![c]+)/gi

    if (shortcutRegex.test(value)) {
      const replacement = value?.length === 3 ? "****" : "\n****";
      selectionRangeVal =
        value?.length === 3 ? target.selectionStart - 1 : target.selectionStart;
      value = target.value?.replace(shortcutRegex, replacement);
    }

    setNoteObj((prev) => {
      const type = event?.nativeEvent?.inputType;
      if (type === "insertLineBreak" || type === "deleteContentBackward") {
        const forwardBool = type === "insertLineBreak" ? true : false;
        const { cursorPos, prevNote } = breakOutOfAsterisk(
          prev?.note,
          forwardBool
        );
        selectionRangeVal = cursorPos;
        return {
          ...prev,
          [name]: prevNote || value,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });

    setSelectionRange(selectionRangeVal);
  };

  const clearError = () => {
    setError("");
  };

  const closeModal = () => {
    setNoteObj({ ...clearedNote });
    setSelectionRange(null);
    clearError();
    close();
  };

  const saveNote = async () => {
    if (noteObj?.note?.length <= 0) {
      setError("Please Enter Note");
      return;
    }

    const dateNow = Date.now().toString();

    const { data } = await updateNoteCall({
      variables: {
        noteId: noteObj?._id,
        noteText: noteObj?.note?.trim() || noteObj?.note,
        date: dateNow,
        userUpdated: userName,
      },
    });

    updateCB(data?.updateClientNote);
  };

  return (
    <BdrModal
      open={open}
      onClose={closeModal}
      title="Update Note"
      size="xl"
      surface="var(--bdr-grad-panel)"
      actions={
        <>
          <BdrButton onClick={closeModal}>Cancel</BdrButton>
          {/* solid: this modal sits on the saturated --bdr-grad-panel */}
          <BdrButton variant="confirm-solid" onClick={saveNote}>
            Save
          </BdrButton>
        </>
      }
    >
      <div className="update-note-modal-shortcut">
        {/* Triple {mediaOver900 ? "'asterisk'" : "'c'"} to start calendar */}
        Triple {"'c'"} to start calendar note note shortcut. e.g. **Hello
        there**
      </div>

      <BdrInput
        inputRef={inputRef}
        label="Update Note"
        name="note"
        onFocus={clearError}
        value={noteObj?.note}
        onChange={handleChange}
        error={error}
        multiline
        rows={5}
      />
    </BdrModal>
  );
};

export default UpdateNoteModal;
